import { useRef } from "react";
import { useEffect } from "react";

export const AnimatedIcon = () => {
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        const W = canvas.width; // 27
        const H = canvas.height; // 16
        const BAR_COUNT = 5;
        const BAR_WIDTH = 3; // px
        const SPACE = 3; // px
        const RADIUS = 1.5; // px border radius

        // Precompute x positions for bars (pattern bar + space ...)
        const barPositions = Array.from({ length: BAR_COUNT }, (_, i) => i * (BAR_WIDTH + SPACE));

        // Keep a smoothed value for each bar so it animates nicely.
        const smoothValues = new Array(BAR_COUNT).fill(0);
        const DECAY = 0.08; // fall speed
        const ATTACK = 0.35; // rise speed (lerp factor when increasing)

        // Helper: draw a rounded rect.
        const drawRoundedRect = (x, y, w, h, r) => {
            // Ensure radius doesn't exceed half-dimensions.
            const rr = Math.min(r, w / 2, h / 2);
            ctx.beginPath();
            ctx.moveTo(x + rr, y);
            ctx.lineTo(x + w - rr, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
            ctx.lineTo(x + w, y + h - rr);
            ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
            ctx.lineTo(x + rr, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
            ctx.lineTo(x, y + rr);
            ctx.quadraticCurveTo(x, y, x + rr, y);
            ctx.closePath();
            ctx.fill();
        };

        // Frequency range mapping: distribute bar ranges on a quasi-log scale for more movement.
        const computeBarAverages = (data) => {
            const n = data.length;
            if (!n) return new Array(BAR_COUNT).fill(0);

            // Create edges using an exponential curve so low & mid frequencies get broader coverage.
            const edges = [];
            for (let i = 0; i <= BAR_COUNT; i++) {
                const t = i / BAR_COUNT;
                // Exponential easing (power 1.6 chosen empirically)
                const e = Math.pow(t, 1.6);
                edges.push(Math.min(n - 1, Math.round(e * (n - 1))));
            }
            const values = [];
            for (let b = 0; b < BAR_COUNT; b++) {
                const start = edges[b];
                const end = edges[b + 1];
                let sum = 0;
                let count = 0;
                for (let i = start; i <= end; i++) {
                    sum += data[i];
                    count++;
                }
                const avg = count ? sum / count : 0;
                // Normalize 0..1
                values.push(avg / 255);
            }
            return values;
        };

        const draw = (bars) => {
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = "#000";
            // Mirror vertically around center line.
            const centerY = H / 2; // 8
            bars.forEach((val, idx) => {
                // Smooth value (attack/decay)
                const current = smoothValues[idx];
                let target = val;
                let next;
                if (target > current) {
                    next = current + (target - current) * ATTACK;
                } else {
                    next = current - (current - target) * DECAY;
                }
                smoothValues[idx] = next;
                // Height scaled: ensure minimum visibility.
                const barHeight = Math.max(2, next * H);
                const y = centerY - barHeight / 2;
                const x = barPositions[idx];
                drawRoundedRect(x, y, BAR_WIDTH, barHeight, RADIUS);
            });
        };

        const analyserListener = ({ data }) => {
            // data is Uint8Array from analyser.
            const bars = computeBarAverages(data);
            draw(bars);
        };

        // Register listener
        if (window.audioplayer) {
            window.audioplayer.addEventListener("analyser", analyserListener);
        }
        return () => {
            if (window.audioplayer) {
                window.audioplayer.removeEventListener("analyser", analyserListener);
            }
        };
    }, []);

    return (
        <canvas ref={ ref } width="27" height="16" />
    );
};
